// Dependencies
import axios, { AxiosError } from 'axios'

// Services
import { getCookie, Auth } from "@/lib/cookie"

// Utils
import { Converter } from '../utils/converter'

/**
 * @typedef ProjectAddress
 * @type {Object}
 * @property {String} name
 */

/**
 * @typedef ProjectName
 * @type {Object}
 * @property {String} tha - Name in Thai
 * @property {String} eng - Name in English
 */

/**
 * @typedef ProjectImage
 * @type {Object}
 * @property {String} icon  - Project Icon
 * @property {String} cover - Project Cover
 */

/**
 * @typedef ProjectData
 * @type {Object}
 * @property {String} id              - Unique ID
 * @property {String} code            - Project Code
 * @property {String} type            - Project Type (NSL or NSH)
 * @property {ProjectName} name       - Project Names
 * @property {ProjectImage} image     - Project Images
 * @property {ProjectAddress} address - Project Address Details
 * @property {String} createdAt       - Creation time
 * @property {String} updatedAt       - Updation time
 */

/**
 * @typedef ListParams
 * @type {Object}
 * @property {String} text  - Search text
 * @property {String} type  - Project Type (NSL, NSH, or ALL)
 * @property {Number} page  - List pagination
 * @property {Number} limit - List limitation
 */

/**
 * @typedef ListItem
 * @type {Object}
 * @property {String} id        - Unique ID
 * @property {String} code      - Project Code
 * @property {String} location  - Project Location (Amphur Name)
 * @property {ProjectName} name - Project Name
 * @property {String} createdAt - Creation time
 */

/**
 * @typedef ListResult
 * @type {Object}
 * @property {Number} total     - Total count
 * @property {ListItem[]} items - Project items
 */

/**
 * @typedef CreateParams
 * @type {Object}
 * @property {String} code            - Project Code
 * @property {String} type            - Project Type (NSL or NSH)
 * @property {ProjectName} name       - Project Names
 * @property {ProjectImage} image     - Project Images
 * @property {ProjectAddress} address - Project Address Details
 */

/**
 * @typedef CreateResult
 * @type {Object}
 * @property {String} id        - Unique ID
 * @property {String} createdAt - Creation time
 */

/**
 * @typedef UpdateParams
 * @type {Object}
 * @property {String} type            - Project Type (NSL or NSH)
 * @property {ProjectName} name       - Project Names
 * @property {ProjectImage} image     - Project Images
 * @property {ProjectAddress} address - Project Address Details
 */

/**
 * @typedef UpdateResult
 * @type {Object}
 * @property {Boolean} updated - Update state
 */

/**
 * @typedef DeleteResult
 * @type {Object}
 * @property {Boolean} deleted - Delete state
 */


export class ProjectNonSc {

  // Request Endpoint
  static endpoint = (
    `${process.env.ADMIN_HOST}/ruejai-backend-admin/api/project/nonsc`
  )

  static headers() {
    return {
      'Authorization': `Bearer ${getCookie(Auth.AccessToken)}`
    }
  }

  static has(data, key = 'result') {
    return Object.keys(data).includes(key)
  }

  /**
   * Non SC Project List
   * @param {ListParams} params
   * @returns {Promise<ListResult>}
   */
  static async list(params = {}) {

    // Request URL
    const url = this.endpoint + '/list'

    // Default Result
    let result = {
      total: 0, items: []
    }

    try {

      // Request Headers
      const headers = this.headers()

      // Request Project List
      const { data } = await axios.get(
        url, { headers, params }
      )

      // Validate Result
      if (this.has(data)) {
        result = Converter.key(data.result)
      }
    }
    catch (error) {
      throw new Error(
        this.error('nonsc-project.list', error)
      )
    }

    return result
  }

  /**
   * Fetch project details
   * 
   * @param {Number} id 
   * @returns {Promise<ProjectData>}
   */
  static async get(id) {
    
    // Request URL
    const url = `${this.endpoint}/${id}`
    
    // Default Result
    let result = null

    try {
      
      // Request Headers
      const headers = this.headers()

      // Request Project Detail
      const { data } = await axios.get(
        url, { headers }
      )

      // Validate Result
      if (this.has(data)) {
        result = Converter.key(data.result)
      }

      // Remove Unused
      delete result.id
      delete result.updatedAt
    }
    catch (error) {
      throw new Error(
        this.error('nonsc-project.get', error)
      )
    }

    return result
  }

  /**
   * Request create project
   * 
   * @param {CreateParams} payload - Create payload
   * @returns {Promise<CreateResult>} Result
   */
  static async create(payload) {

    // Request URL
    const url = this.endpoint
    
    // Default Result
    let result = null

    try {
      
      // Request Headers
      const headers = this.headers()

      // Request Create Project
      const { data } = await axios.post(
        url, payload, { headers }
      )

      // Validate Result
      if (this.has(data)) {
        result = data.result
      }

      console.log(result)
    }
    catch (error) {
      throw new Error(
        this.error('nonsc-project.create', error)
      )
    }

    return result
  }

  /**
   * Request update project
   * 
   * @param {Number} id             - Project ID 
   * @param {UpdateParams} payload  - Update payload
   * @returns {Promise<UpdateResult>} Result
   */
  static async update(id, payload) {

    // Request URL
    const url = `${this.endpoint}/${id}`
    
    // Default Result
    let result = null

    try {
      
      // Request Headers
      const headers = this.headers()

      // Request Update Project
      const { data } = await axios.put(
        url, payload, { headers }
      )

      // Validate Result
      if (this.has(data)) {
        result = data.result
      }
    }
    catch ({ message }) {
      throw new Error(
        this.error('nonsc-project.update', error)
      )
    }

    return result
  }

  /**
   * Request delete project
   * 
   * @param {Number} id - Project ID
   * @returns {Promise<DeleteResult>} Result
   */
  static async remove(id) {

    // Request URL
    const url = `${this.endpoint}/${id}`
    
    // Default Result
    let result = null

    try {
      
      // Request Headers
      const headers = this.headers()

      // Request Delete Project
      const { data } = await axios.delete(
        url, { headers }
      )

      // Validate Result
      if (this.has(data)) {
        result = data.result
      }
    }
    catch (error) {
      throw new Error(
        this.error('nonsc-project.remove', error)
      )
    }

    return result
  }

  /**
   * Error response handler
   * 
   * @param {String} action     - Action name
   * @param {AxiosError} error  - Request error
   * @returns {String} Error Code
   */
  static error(action, error) {

    // Validate Response
    if (!error) return

    // Error Props
    const { message, response } = error

    // Error Info
    console.warn(action, message)

    // Error Message
    if (!response) return message

    // Response Props
    const { data } = response

    // Response Error
    if (data && data.message) {
      return data.message || message
    }
  }

  static isLatitude(input) {
    return (
      /^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?)$/.test(input)
    )
  }

  static isLongitude(input) {
    return (
      /^[-+]?((1[0-7]\d|[1-9]?\d)(\.\d+)?|180(\.0+)?)$/.test(input)
    )
  }

  static isURL(input) {
    return (
      /^(http|https)(\:\/{2})([^\W+]\S+)$/.test(input)
    )
  }

  static isLocale(code, input) {
    
    // RegExp
    const regex = {
      chi: /^[\u4E00-\u9FFF\d\-\.\s]+$/gm,
      eng: /^[a-zA-Z\d\-\.\s]+$/gm,
      tha: /^[\u0E01-\u0E5B\d\-\.\s]+$/gm,
    }

    // Input Validator
    const tester = regex[code]

    return (
      tester ? tester.test(input) : false
    )
  }
}


// WEBPACK FOOTER //
// ./src/clients/BackendAdminClient/project-nonsc.js